import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import type {
  CreateAnalysisRequestDto,
  CreateAnalysisResponseDto,
} from '@inference.engine/api-client/web';
import { ANALYSIS_QUERY_KEYS, type AnalysisListItem } from '@/entities/analysis';
import { ROUTES } from '@/shared/config/routes';
import { notificationService } from '@/lib/services/notification.service';

import { createAnalysis, deleteAnalysis, retryAnalysis } from '../../api/analysis.api';
import { useAnalysisNotificationStore } from '../analysisNotificationStore';
import { useTranslation } from 'react-i18next';

// Read hooks (useAnalysis/useAnalyses) moved to `entities/analysis` as
// `analysisQuery`/`analysesQuery`. The mutation hooks below stay feature-owned
// and invalidate `ANALYSIS_QUERY_KEYS.all` so the entity's list query refetches.

export const useCreateAnalysis = () => {
  const { t } = useTranslation('analysis');

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<CreateAnalysisResponseDto, Error, CreateAnalysisRequestDto>({
    mutationFn: createAnalysis,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ANALYSIS_QUERY_KEYS.all });
      useAnalysisNotificationStore.getState().setInitiated();
      void navigate(ROUTES.IQ);
    },
    onError: (error) => {
      notificationService.showError(
        error.message || t('analysis.useAnalyses.failedToCreateAnalysis'),
      );
    },
  });
};

export function useAnalysisCompletionDetection(analyses: AnalysisListItem[] | undefined) {
  const previousStatusesRef = useRef<Map<string, string>>(new Map());
  const addCompleted = useAnalysisNotificationStore((s) => s.addCompleted);
  const decrementInitiated = useAnalysisNotificationStore((s) => s.decrementInitiated);

  useEffect(() => {
    if (!analyses) return;

    const prev = previousStatusesRef.current;
    for (const analysis of analyses) {
      const prevStatus = prev.get(analysis.id);
      if (prevStatus && (prevStatus === 'pending' || prevStatus === 'running')) {
        if (analysis.status === 'completed') {
          addCompleted(analysis.id, analysis.name);
          decrementInitiated();
        } else if (analysis.status === 'failed') {
          decrementInitiated();
        }
      }
    }

    const next = new Map<string, string>();
    for (const analysis of analyses) {
      next.set(analysis.id, analysis.status);
    }
    previousStatusesRef.current = next;
  }, [analyses, addCompleted, decrementInitiated]);
}

export const useRetryAnalysis = () => {
  const { t } = useTranslation('analysis');

  const queryClient = useQueryClient();

  return useMutation<CreateAnalysisResponseDto, Error, string>({
    mutationFn: retryAnalysis,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ANALYSIS_QUERY_KEYS.all });
      notificationService.showSuccess(t('analysis.useAnalyses.analysisGenerationRestarted'));
    },
    onError: (error) => {
      notificationService.showError(
        error.message || t('analysis.useAnalyses.failedToRetryAnalysis'),
      );
    },
  });
};

export const useDeleteAnalysis = () => {
  const { t } = useTranslation('analysis');

  const queryClient = useQueryClient();

  return useMutation<undefined, Error, string>({
    mutationFn: deleteAnalysis,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ANALYSIS_QUERY_KEYS.all });
      notificationService.showSuccess(t('analysis.useAnalyses.analysisDeleted'));
    },
    onError: (error) => {
      notificationService.showError(
        error.message || t('analysis.useAnalyses.failedToDeleteAnalysis'),
      );
    },
  });
};
